<script setup lang="ts">
import { useRouter } from 'vue-router';
import Separator from "./ui/separator/Separator.vue";

const router = useRouter();

// 处理导航点击
const handleNavClick = (href: string, event: Event) => {
  if (href.startsWith('/#')) {
    // 锚点链接
    event.preventDefault();
    const targetId = href.substring(2);
    
    // 如果不在首页，先跳转到首页
    if (router.currentRoute.value.path !== '/') {
      router.push('/').then(() => {
        setTimeout(() => {
          const element = document.getElementById(targetId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      });
    } else {
      // 已经在首页，直接滚动
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  } else if (href.startsWith('/')) {
    // 路由链接
    event.preventDefault();
    router.push(href);
  }
};
</script>

<template>
  <footer id="footer" class="container py-24 pb-16 sm:py-32 sm:pb-24" itemscope itemtype="https://schema.org/WPFooter">
    <div class="p-10 bg-muted/50 dark:bg-card border rounded-2xl">
      <div class="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-x-12 gap-y-8">
        <div class="col-span-full xl:col-span-2" itemscope itemtype="https://schema.org/Organization">
          <a href="/" class="flex font-bold items-center" @click="handleNavClick('/', $event)" aria-label="返回首页">
            <img src="@/assets/icon.ico" alt="创诚纪元人工智能科技Logo - 物联网+AI解决方案" class="w-9 h-9 mr-2" itemprop="logo" />
            <h3 class="text-2xl" itemprop="name">创诚智能</h3>
          </a>
          <p class="mt-2 text-sm text-muted-foreground max-w-xs" itemprop="description">
            专注于物联网+AI技术融合，为企业提供专业的智能化解决方案。
          </p>
        </div>

        <nav class="flex flex-col gap-2" aria-label="产品服务导航">
          <h3 class="font-bold text-lg">产品服务</h3>
          <div>
            <a 
              href="/products/energy-management" 
              @click="handleNavClick('/products/energy-management', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="智能能耗管理系统 - 实时监控与优化"
            >
              智能能耗管理
            </a>
          </div>

          <div>
            <a 
              href="/products/industrial-automation" 
              @click="handleNavClick('/products/industrial-automation', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="工业自动化控制 - PLC与SCADA系统"
            >
              工业自动化
            </a>
          </div>

          <div>
            <a 
              href="/products/intelligent-instruments" 
              @click="handleNavClick('/products/intelligent-instruments', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="智能仪器仪表 - 高精度检测设备"
            >
              环境监测
            </a>
          </div>

          <div>
            <a 
              href="/products/intelligent-instruments" 
              @click="handleNavClick('/products/intelligent-instruments', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="智能仪器仪表 - 精密测量与数据采集"
            >
              智能仪器仪表
            </a>
          </div>
        </nav>

        <nav class="flex flex-col gap-2" aria-label="解决方案导航">
          <h3 class="font-bold text-lg">解决方案</h3>
          <div>
            <a 
              href="/products/ai-solutions" 
              @click="handleNavClick('/products/ai-solutions', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="边缘计算解决方案 - 低延迟实时处理"
            >
              边缘计算
            </a>
          </div>

          <div>
            <a 
              href="/products/ai-solutions" 
              @click="handleNavClick('/products/ai-solutions', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="智能数据分析 - AI驱动的数据洞察"
            >
              数据分析
            </a>
          </div>

          <div>
            <a 
              href="/products/enterprise-software" 
              @click="handleNavClick('/products/enterprise-software', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="云端集成服务 - 无缝连接云平台"
            >
              云端集成
            </a>
          </div>

          <div>
            <a 
              href="/#benefits" 
              @click="handleNavClick('/#benefits', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="核心技术优势 - 创新科技引领未来"
            >
              技术优势
            </a>
          </div>
        </nav>

        <nav class="flex flex-col gap-2" aria-label="资源中心导航">
          <h3 class="font-bold text-lg">资源中心</h3>
          <div>
            <a 
              href="/blog" 
              @click="handleNavClick('/blog', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="技术博客 - 物联网AI前沿技术分享"
            >
              技术博客
            </a>
          </div>

          <div>
            <a 
              href="/#faq" 
              @click="handleNavClick('/#faq', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="常见问题解答 - 快速找到答案"
            >
              常见问题
            </a>
          </div>

          <div>
            <a 
              href="/#testimonials" 
              @click="handleNavClick('/#testimonials', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="客户案例与评价 - 真实客户反馈"
            >
              客户案例
            </a>
          </div>

          <div>
            <a 
              href="/#contact" 
              @click="handleNavClick('/#contact', $event)"
              class="opacity-60 hover:opacity-100 transition-opacity"
              title="联系我们 - 获取专业咨询服务"
            >
              联系我们
            </a>
          </div>
        </nav>

        <address class="flex flex-col gap-2 not-italic" itemscope itemtype="https://schema.org/Organization">
          <h3 class="font-bold text-lg">联系信息</h3>
          <div class="text-sm text-muted-foreground space-y-1">
            <div itemprop="telephone">
              <span class="opacity-60">电话：</span>
              <a href="tel:13360971050" class="hover:text-primary transition-colors">13360971050</a>
            </div>
            <div itemprop="email">
              <span class="opacity-60">邮箱：</span>
              <a href="mailto:924121070@qq.com" class="hover:text-primary transition-colors">927121070@qq.com</a>
            </div>
            <div itemprop="address" itemscope itemtype="https://schema.org/PostalAddress">
              <span class="opacity-60">地址：</span>
              <span itemprop="streetAddress">深圳市龙华区汇德大厦12楼</span>
            </div>
            <div>
              <span class="opacity-60">工作时间：</span>
              <time itemprop="openingHours" content="Mo-Fr 08:30-18:00">周一至周五 8:30-18:00</time>
            </div>
          </div>
        </address>
      </div>

      <Separator class="my-6" />
      <section class="flex flex-col md:flex-row justify-between items-center gap-4">
        <div class="text-sm text-muted-foreground space-y-1">
          <p>&copy; 2024 创诚人工智能科技有限公司。保留所有权利。</p>
          <p>
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer"
              class="opacity-60 hover:opacity-100 transition-opacity">
              粤ICP备2025478209号-1
            </a>
          </p>
        </div>
        <nav class="flex gap-6 text-sm text-muted-foreground" aria-label="法律信息">
          <a href="/about" @click="handleNavClick('/about', $event)" class="opacity-60 hover:opacity-100 transition-opacity" title="隐私政策">隐私政策</a>
          <a href="/about" @click="handleNavClick('/about', $event)" class="opacity-60 hover:opacity-100 transition-opacity" title="服务条款">服务条款</a>
          <a href="/blog" @click="handleNavClick('/blog', $event)" class="opacity-60 hover:opacity-100 transition-opacity" title="网站地图">网站地图</a>
        </nav>
      </section>
    </div>
  </footer>
</template>

<style scoped>
/* 主题相关的Logo颜色切换 */
.theme-logo {
  transition: filter 0.3s ease;
}

/* 浅色模式下的Logo（黑色） */
:root .theme-logo {
  filter: brightness(0) saturate(100%);
}

/* 深色模式下的Logo（白色） */
:root.dark .theme-logo {
  filter: brightness(0) saturate(100%) invert(1);
}

/* 替代方案：直接使用类名控制 */
.light .theme-logo {
  filter: brightness(0) saturate(100%);
}

.dark .theme-logo {
  filter: brightness(0) saturate(100%) invert(1);
}
</style>
